import React, { useContext } from 'react';
import { Case } from '../../../../services/buildfire/rdb/cases';
import { useBuildfireSettings } from '../../../../context/SettingsContext';
import { useActor, useInterpret } from '@xstate/react';
import { editPaymentMachine } from '../../../../stateMachines/editPayment';
import { PaymentType } from '../../../../stateMachines/submitCase/selectPaymentType';
import { useFirebase } from '../../../../services/firebase';
import CreditCard from './CreditCard';
import ProgressBar from '../../../common/ProgressBar';
import SelectPaymentType from './SelectPaymentType';
import { IonButton, useIonToast } from '@ionic/react';
import AlertBox from '../../../common/AlertBox';
import styled from 'styled-components';
import CaseContext from '../../../../services/buildfire/rdb/CaseContext';
import useGoBackToCase from '../../../../hooks/useGoBackToCase';

interface EditPaymentProps {
  case: Case;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;
const Content = styled.div`
  max-width: 600px;
  width: 100%;
`;
const ErrorContainer = styled.div`
  margin: 64px 0;
  padding: 0 32px;
`;

const EditPayment: React.FC<EditPaymentProps> = ({ case: c }) => {
  const { currentUser, app, parentAccount } = useFirebase();
  const { settings } = useBuildfireSettings();
  const editPaymentService = useInterpret(editPaymentMachine, {
    context: {
      firebaseApp: app,
      stripePublishableKey: settings.stripe.publishableKey,
      userId: currentUser?.uid,
      email: currentUser?.email!,
      name: currentUser?.displayName!,
      parentAccount,
      case: c,
      paymentType: c.invoiceAccount
        ? PaymentType.INVOICE
        : PaymentType.CREDIT_CARD,
    },
  });
  const [state, send] = useActor(editPaymentService);
  const goBack = useGoBackToCase();
  const [present] = useIonToast();
  const { updateCase } = useContext(CaseContext);

  React.useEffect(() => {
    const onDone = () => {
      const { id } = editPaymentService.state.context.case!;
      updateCase(editPaymentService.state.context.case!);
      goBack(c.id);
      present({
        message: 'Payment method updated successfully.',
        duration: 5000,
        position: 'bottom',
      });
    };
    editPaymentService.onDone(onDone);
    return () => {
      editPaymentService.off(onDone);
    };
  }, []);

  if (!currentUser) {
    throw new Error('ERROR not logged in');
  }

  const spreadProps = { currentUser };
  const isLoading =
    state.matches({ selectPaymentType: 'load' }) ||
    state.matches({ creditCard: 'load' }) ||
    state.matches({ creditCard: 'confirmSetupIntent' }) ||
    state.matches('submit');
  const hasPastDueInvoice = c.invoiceAccount
    ? false
    : state.context.hasPastDueInvoice;
  (window as any).editPaymentService = editPaymentService;

  if (isLoading) {
    return (
      <Container>
        <ProgressBar indeterminate={isLoading} />
      </Container>
    );
  }

  return (
    <Container>
      <Content>
        {state.matches('selectPaymentType') && (
          <SelectPaymentType {...spreadProps} state={state} send={send} />
        )}
        {state.matches('creditCard') && (
          <CreditCard {...spreadProps} state={state} send={send} />
        )}
        {state.matches('error') && (
          <ErrorContainer>
            <AlertBox
              text="An unknown error occurred. Please try again."
              type="error"
            />
            <IonButton expand="block" onClick={() => goBack(c.id)}>
              Back
            </IonButton>
          </ErrorContainer>
        )}
      </Content>
    </Container>
  );
};

export default EditPayment;
